import { useEffect, useState } from 'react';

import Btn from '../ui/Btn';
import { Input, Spinner } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { supabase } from '../../lib/supabase';
import { PORTAL_URL } from '../../lib/config';
import { useIsMobile } from '../../lib/useBreakpoint';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * ForgotPasswordModal — small centered modal (matches ConfirmModal styling)
 * that kicks off the Supabase password-reset email.
 *
 * Security: after submitting we ALWAYS show the same neutral confirmation, so
 * we never reveal whether an account exists for the entered email. The only
 * error we surface is rate-limiting (HTTP 429) — that leaks nothing about
 * existence but stops a confused user from spamming the button.
 *
 * Props:
 *   open          — boolean, mount/show
 *   initialEmail  — prefill (whatever's already typed in the sign-in form)
 *   onClose()     — close handler (Cancel / Done / X / ESC / backdrop click)
 */
export default function ForgotPasswordModal({ open, initialEmail = '', onClose }) {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed each time the modal opens: pull in the latest typed email and clear
  // any result/error from a previous open.
  useEffect(() => {
    if (!open) return;
    setEmail(initialEmail);
    setSent(false);
    setError(null);
    setSubmitting(false);
  }, [open, initialEmail]);

  // ESC to close + body scroll lock (mirrors ConfirmModal)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError(null);

    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(clean, {
        redirectTo: `${PORTAL_URL}/reset-password`,
      });
      // Surface ONLY rate-limiting. Every other outcome — success, or even
      // "no such user" — shows the identical confirmation so we never disclose
      // whether the address is registered.
      if (resetErr && (resetErr.status === 429 || /rate|too many|seconds/i.test(resetErr.message || ''))) {
        setError('You requested a reset link recently. Please wait a minute and try again.');
      } else {
        setSent(true);
      }
    } catch (err) {
      // Network/unexpected error: still don't leak existence — show the same
      // confirmation. The user can retry from the email if nothing arrives.
      console.error('Password reset request failed:', err);
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0,
    background: 'rgba(10, 15, 46, 0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: isMobile ? 0 : 20,
    animation: 'fadeIn 0.2s ease',
  };

  const cardStyle = isMobile
    ? {
        background: 'var(--surface)',
        borderTopLeftRadius: 'var(--r-lg)',
        borderTopRightRadius: 'var(--r-lg)',
        width: '100%',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border)',
        padding: '24px 20px 28px',
        boxSizing: 'border-box',
        maxHeight: '90dvh',
        overflowY: 'auto',
      }
    : {
        background: 'var(--surface)',
        borderRadius: 'var(--r-lg)',
        maxWidth: 440, width: '100%',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border)',
        padding: '28px 28px 24px',
        boxSizing: 'border-box',
      };

  // Backdrop click closes only when the click target is the overlay itself.
  const onOverlayClick = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div onClick={onOverlayClick} role="dialog" aria-modal="true" aria-labelledby="fp-title" style={overlayStyle}>
      <div style={cardStyle}>
        {sent ? (
          // ── Confirmation state — identical regardless of whether the email exists ──
          <>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'var(--ok-bg)', color: 'var(--ok)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 14,
            }}>
              <Icon.check size={22} />
            </div>
            <h2 id="fp-title" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', margin: 0, marginBottom: 8 }}>
              Check your email
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.55, margin: 0, marginBottom: 22 }}>
              If an account exists for{' '}
              <strong style={{ color: 'var(--text)' }}>{email.trim().toLowerCase()}</strong>, we've sent a link
              to reset your password. The link expires in 1 hour.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn
                variant="primary"
                size="md"
                onClick={onClose}
                style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
              >
                Done
              </Btn>
            </div>
          </>
        ) : (
          // ── Request state ──
          <form onSubmit={submit} noValidate>
            <h2 id="fp-title" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', margin: 0, marginBottom: 8 }}>
              Reset your password
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.55, margin: 0, marginBottom: 18 }}>
              Enter the email you use for MagicIntake and we'll send you a link to set a new password.
            </p>

            {error && (
              <div style={{
                padding: '10px 12px',
                background: 'var(--danger-bg)',
                border: '1px solid rgba(239,68,68,0.22)',
                borderRadius: 10, color: 'var(--danger)',
                fontSize: 12.5, lineHeight: 1.45,
                display: 'flex', alignItems: 'flex-start', gap: 8,
                marginBottom: 14,
              }}>
                <Icon.alert size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{error}</span>
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
              Work email
            </label>
            <Input
              icon={Icon.mail}
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              style={{ marginBottom: 20 }}
              disabled={submitting}
            />

            <div style={{
              display: 'flex', gap: 8,
              flexDirection: isMobile ? 'column-reverse' : 'row',
              justifyContent: 'flex-end',
            }}>
              <Btn
                variant="outline"
                size="md"
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
              >
                Cancel
              </Btn>
              <Btn
                variant="primary"
                size="md"
                type="submit"
                onClick={submit}
                disabled={submitting}
                iconRight={submitting ? null : Icon.arrowR}
                style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
              >
                {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Sending…</> : 'Send reset link'}
              </Btn>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
